/**
 * GuildAudit.jsx — Admin widget: 13-day guild performance + dues compliance.
 *
 * Modernized with ThemedTable:
 *   - Sparkline and summary stats
 *   - Sortable underperformer tables with dues beacons and user pills
 */
import { Show, For, createMemo } from 'solid-js';
import useWidgetData from './useWidgetData.js';
import { gold, qty } from '../lib/format.js';
import EMOJI from '../lib/emoji.js';
import ThemedTable, { GoldCell, UserCell } from '../components/ThemedTable/index.js';

const GUILD_ORDER = ['post', 'caravan', 'dealers'];

/** Mini bar-chart sparkline for a 13-element trend array. */
function Sparkline(props) {
  const trend  = () => props.trend || [];
  const maxVal = createMemo(() => Math.max(...trend().map(d => d.gold || 0), 1));
  return (
    <div class="v2-gaudit__spark" title="13-day gold trend" aria-hidden="true">
      <For each={trend()}>{d => (
        <div
          class="v2-gaudit__spark-bar"
          style={{ height: `${Math.max(2, Math.round((d.gold / maxVal()) * 100))}%` }}
          title={`${d.date}: ${gold(d.gold)}`}
        />
      )}</For>
    </div>
  );
}

function duesDesc(key, rule) {
  if (key === 'post')    return `≥1 sale or raffle entry / ${rule.windowDays} days`;
  if (key === 'caravan') return `≥1 sale / ${rule.windowDays} days`;
  return `≥25,000g (sales + raffle) / ${rule.windowDays} days`;
}

function GuildPanel(props) {
  const g    = () => props.data || {};
  const key  = () => props.guildKey;
  const ups  = createMemo(() => g().underperformers || []);
  const isDealers = () => key() === 'dealers';

  const columns = createMemo(() => {
    const cols = [
      {
        key: 'sellerName',
        label: 'Player',
        sortable: true,
        type: 'string',
        canHide: false,
        render: (_v, u) => (
          <div style={{ display: 'inline-flex', 'align-items': 'center', gap: '6px' }}>
            <UserCell name={u.sellerName} />
            <Show when={!u.duesMet}>
              <span
                class="v2-tt-beacon-dot v2-tt-beacon-dot--error"
                title="Missing dues"
                style={{ width: '6px', height: '6px' }}
              />
            </Show>
          </div>
        ),
      },
      {
        key: 'saleCount',
        label: 'Sales',
        sortable: true,
        type: 'number',
        align: 'right',
        getValue: u => u.saleCount || 0,
        render: (v) => <span class="v2-tt-mono">{qty(v)}</span>,
      },
      {
        key: 'goldTotal',
        label: 'Gold',
        sortable: true,
        type: 'currency',
        align: 'right',
        getValue: u => u.goldTotal || 0,
        render: (v) => <GoldCell value={v} />,
      },
    ];

    if (isDealers()) {
      cols.push(
        {
          key: 'raffleGold',
          label: 'Raffle',
          sortable: true,
          type: 'currency',
          align: 'right',
          getValue: u => u.raffleGold || 0,
          render: (v) => v > 0 ? <GoldCell value={v} /> : <span class="v2-tt-dim">—</span>,
        },
        {
          key: 'combined',
          label: 'Combined',
          sortable: true,
          type: 'currency',
          align: 'right',
          getValue: u => (u.goldTotal || 0) + (u.raffleGold || 0),
          render: (_v, u) => {
            const c = (u.goldTotal || 0) + (u.raffleGold || 0);
            return (
              <span style={{ color: c >= 25000 ? 'var(--t-green, #4ade80)' : 'var(--bad, #f87171)' }}>
                <GoldCell value={c} />
              </span>
            );
          },
        }
      );
    }

    cols.push({
      key: 'daysSinceActivity',
      label: 'Last Act.',
      sortable: true,
      type: 'number',
      align: 'right',
      getValue: u => u.daysSinceActivity ?? 9999,
      render: (v) => <span class="v2-tt-mono v2-tt-dim">{v != null ? `${v}d ago` : '—'}</span>,
    });

    return cols;
  });

  return (
    <div class="v2-gaudit__panel">
      <div class="v2-gaudit__panel-hdr">
        <span class="v2-gaudit__guild-name">{g().guildName}</span>
        <span class="v2-gaudit__dues-desc">{duesDesc(key(), g().duesRule || {})}</span>
      </div>

      <Sparkline trend={g().trend || []} />

      <div class="v2-gaudit__stats">
        <div class="v2-gaudit__stat">
          <span class="v2-gaudit__stat-val v2-gaudit__gold">{gold(g().totalGold || 0)}</span>
          <span class="v2-gaudit__stat-lbl">13-day gold</span>
        </div>
        <div class="v2-gaudit__stat">
          <span class="v2-gaudit__stat-val">{qty(g().totalSales || 0)}</span>
          <span class="v2-gaudit__stat-lbl">sales</span>
        </div>
        <div class="v2-gaudit__stat">
          <span class="v2-gaudit__stat-val">{g().activeSellers || 0}</span>
          <span class="v2-gaudit__stat-lbl">active sellers</span>
        </div>
        <Show when={g().biddingPower > 0}>
          <div class="v2-gaudit__stat">
            <span class="v2-gaudit__stat-val v2-gaudit__gold">{gold(g().biddingPower || 0)}</span>
            <span class="v2-gaudit__stat-lbl">30d bidding power</span>
          </div>
        </Show>
        <div class="v2-gaudit__stat">
          <span class={`v2-gaudit__stat-val${ups().length > 0 ? ' v2-gaudit__warn' : ' v2-gaudit__ok'}`}>
            {ups().length}
          </span>
          <span class="v2-gaudit__stat-lbl">underperformers</span>
        </div>
      </div>

      <Show when={g().topSellers?.length}>
        <details class="v2-collapsible" style={{ 'margin-bottom': '6px' }}>
          <summary class="v2-collapsible__summary">🏆 Top Sellers (13d · {qty(g().topSellers.length)})</summary>
          <div class="v2-collapsible__body">
            <ol class="v2-rank-list">
              <For each={g().topSellers.slice(0, 5)}>{(ts, idx) => (
                <li class="v2-rank-row">
                  <span class="v2-rank-row__medal">{idx() + 1}</span>
                  <UserCell name={ts.sellerName} />
                  <span class="v2-rank-row__sub" style={{ 'margin-left': 'auto' }}>{qty(ts.saleCount)} sales</span>
                  <span class="v2-rank-row__value">{gold(ts.goldTotal)}</span>
                </li>
              )}</For>
            </ol>
          </div>
        </details>
      </Show>
      <div class="v2-gaudit__under-hdr">
        Underperformers
        <span class="v2-gaudit__under-note"> (worst → best)</span>
      </div>

      <Show when={ups().length} fallback={
        <div class="v2-gaudit__ok-msg">All members meeting dues {EMOJI.ok}</div>
      }>
        <div style={{ 'margin-top': '6px', 'min-height': '180px' }}>
          <ThemedTable
            columns={columns()}
            rows={ups()}
            storageKey={`guild_audit_${key()}`}
            defaultSortKey="goldTotal"
            defaultSortDir="asc"
            searchPlaceholder="Filter underperformers…"
            emptyText="No underperformers match."
            compact={true}
          />
        </div>
      </Show>
    </div>
  );
}

export default function GuildAudit() {
  const { data, loading, error, refetch } = useWidgetData('redfur-audit', () => ({}));

  return (
    <div class="v2-widget v2-gaudit">
      <div class="v2-widget-hdr">
        <span class="v2-widget-hdr__title">⚔ Guild Dues Compliance Audit</span>
        <button
          type="button"
          class="v2-widget-hdr__btn"
          style={{ 'margin-left': 'auto' }}
          title="Refresh"
          onClick={() => refetch()}
        >↻</button>
      </div>

      <Show when={!loading()} fallback={<div class="v2-widget-skel">Loading audit data…</div>}>
        <Show when={!error()} fallback={<div class="v2-error">{error()}</div>}>
          <div class="v2-gaudit__body">
            <For each={GUILD_ORDER}>{key => (
              <Show when={data() && data()[key]}>
                <GuildPanel guildKey={key} data={data()[key]} />
              </Show>
            )}</For>
          </div>
        </Show>
      </Show>
    </div>
  );
}
